import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import '../marketer_style.css';

const REMEMBER_KEY = 'haseela_mkt_rc';
const CIPHER_KEY = 'Hs@2026#MktPnl!Sec';

const encryptCredentials = (data) => {
    const json = JSON.stringify(data);
    let result = '';
    for (let i = 0; i < json.length; i++) {
        result += String.fromCharCode(json.charCodeAt(i) ^ CIPHER_KEY.charCodeAt(i % CIPHER_KEY.length));
    }
    return btoa(result);
};

const decryptCredentials = (encrypted) => {
    try {
        const decoded = atob(encrypted);
        let result = '';
        for (let i = 0; i < decoded.length; i++) {
            result += String.fromCharCode(decoded.charCodeAt(i) ^ CIPHER_KEY.charCodeAt(i % CIPHER_KEY.length));
        }
        return JSON.parse(result);
    } catch {
        return null;
    }
};

const MarketerLogin = ({ setIsAuthenticated }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ username: '', password: '' });
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        document.documentElement.setAttribute('dir', 'rtl');
        document.documentElement.setAttribute('lang', 'ar');
        document.title = 'حصيلة - لوحة المورد';

        // Load saved credentials
        const saved = localStorage.getItem(REMEMBER_KEY);
        if (saved) {
            const creds = decryptCredentials(saved);
            if (creds) {
                setFormData({ username: creds.u || '', password: creds.p || '' });
                setRememberMe(true);
            }
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errorMessage) setErrorMessage('');
    };

    const validateForm = () => {
        if (!formData.username.trim() || !formData.password.trim()) {
            setErrorMessage('اسم المستخدم وكلمة المرور مطلوبة');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const response = await axiosInstance.post(`/haseela/marketer/login/`, {
                username: formData.username,
                password: formData.password
            });

            if (response.data.jwt) {
                if (rememberMe) {
                    localStorage.setItem(REMEMBER_KEY, encryptCredentials({ u: formData.username, p: formData.password }));
                } else {
                    localStorage.removeItem(REMEMBER_KEY);
                }

                localStorage.setItem('haseela_marketerToken', response.data.jwt);
                setSuccessMessage('تم تسجيل الدخول بنجاح! جاري التحويل...');
                setIsAuthenticated?.(true);
                navigate('/dashboard', { replace: true });
            }
        } catch (error) {
            const errorMsg = error.response?.data?.detail ||
                           error.response?.data?.error ||
                           'اسم المستخدم أو كلمة المرور غير صحيحة';
            setErrorMessage(errorMsg);
            setFormData(prev => ({ ...prev, password: '' }));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-page" style={{ backgroundImage: 'url(/login_bg.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <img src="/haseela_logo.png" alt="Haseela" className="admin-logo" />
                        <p className="admin-subtitle">لوحة المورد</p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-form">
                        <div className="form-group">
                            <label htmlFor="username" className="form-label">
                                <i className="fas fa-user"></i>
                                <span>اسم المستخدم</span>
                            </label>
                            <input
                                type="text"
                                id="username"
                                name="username"
                                className="form-input"
                                placeholder="أدخل اسم المستخدم"
                                value={formData.username}
                                onChange={handleInputChange}
                                autoComplete="username"
                                disabled={isLoading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password" className="form-label">
                                <i className="fas fa-lock"></i>
                                <span>كلمة المرور</span>
                            </label>
                            <div className="password-field">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    name="password"
                                    className="form-input"
                                    placeholder="أدخل كلمة المرور"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    autoComplete="current-password"
                                    disabled={isLoading}
                                    style={{ backgroundImage: 'none' }}
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    disabled={isLoading}
                                >
                                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                            </div>
                        </div>

                        <label className="remember-me">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                disabled={isLoading}
                            />
                            <span>تذكرني</span>
                        </label>

                        <button
                            type="submit"
                            className={`login-btn ${isLoading ? 'loading' : ''}`}
                            disabled={isLoading}
                        >
                            <i className="fas fa-sign-in-alt"></i>
                            <span>تسجيل الدخول</span>
                            {isLoading && (
                                <div className="btn-loader">
                                    <i className="fas fa-spinner fa-spin"></i>
                                </div>
                            )}
                        </button>

                        {errorMessage && (
                            <div className="error-message">
                                <i className="fas fa-exclamation-triangle"></i>
                                <span>{errorMessage}</span>
                            </div>
                        )}

                        {successMessage && (
                            <div className="success-message">
                                <i className="fas fa-check-circle"></i>
                                <span>{successMessage}</span>
                            </div>
                        )}
                    </form>

                    <div className="login-footer">
                        <p className="copyright">© 2026 حصيلة. جميع الحقوق محفوظة.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MarketerLogin;
